import React, { useState } from 'react';
import { Compass, Clock, Bus, User, Utensils, ChevronLeft, ChevronRight } from 'lucide-react';
import { StorytellingLayout, ExpandableCard } from '../base';

/**
 * Tela de storytelling para passeios/tours
 * Responsabilidade: Exibir informações de passeios (pode ter múltiplos)
 * 
 * Props:
 * - tours: array de blocos de tour
 * - stepIndicator: "Passo X de Y"
 * - narrativeText: texto emocional (pode variar por tour)
 * - ctaText: texto do botão
 */
export default function StorytellingTour({
  tours = [],
  stepIndicator,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const [currentTourIndex, setCurrentTourIndex] = useState(0);
  
  // Se recebeu um bloco único, converte para array
  const tourList = Array.isArray(tours) ? tours : [tours];
  const currentTour = tourList[currentTourIndex] || {};
  const details = currentTour.details || {};
  
  // Imagens - verifica image_url no bloco e nos details
  const defaultImages = [
    'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&q=80',
    'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=800&q=80'
  ];
  
  // Prioridade: image_url do bloco > details.image_url > images array > default
  const primaryImage = currentTour.image_url || details.image_url || null;
  const imagesArray = currentTour.images || details.images || [];
  const images = primaryImage ? [primaryImage, ...imagesArray] : (imagesArray.length > 0 ? imagesArray : defaultImages);

  // Verifica se transfer está incluído baseado no array 'includes'
  const includes = details.includes || [];
  const notIncludes = details.not_includes || [];
  
  const hasTransfer = includes.some(item => 
    item.toLowerCase().includes('transfer') || 
    item.toLowerCase().includes('transporte')
  );
  
  const hasGuide = includes.some(item => 
    item.toLowerCase().includes('guia')
  );
  
  // Extrai tipo de guia do array includes
  const guideItem = includes.find(item => item.toLowerCase().includes('guia'));
  const guideType = guideItem || (hasGuide ? 'Incluído' : 'Não incluído');

  // Navegação entre tours
  const goToNext = () => {
    setCurrentTourIndex((prev) => (prev + 1) % tourList.length);
  };

  const goToPrev = () => {
    setCurrentTourIndex((prev) => (prev - 1 + tourList.length) % tourList.length);
  };

  // Formata data
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  // Narrativa contextual por tour
  const narrativeText = details.narrative || currentTour.narrative || 
    'Uma experiência única aguarda por você. Momentos que vão ficar guardados para sempre na memória.';

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header: Ícone + Título + Data */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Compass size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ 
            fontSize: '1.0625rem', 
            fontWeight: 600, 
            color: 'white', 
            margin: 0,
            lineHeight: 1.3
          }}>
            {details.name || details.full_name || currentTour.title || 'Passeio'}
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0.25rem 0 0 0' }}>
            Data: {formatDate(currentTour.date)}
          </p>
        </div>
      </div>

      {/* Informações principais */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '1rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Duração */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
              {details.duration || 'N/A'}
            </p>
            <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Duração
            </p>
          </div>
        </div>

        {/* Transfer */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Bus size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
              {hasTransfer ? 'Incluído' : 'Não incluído'}
            </p>
            <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Transfer
            </p>
          </div>
        </div>

        {/* Guia */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <User size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
              {guideType}
            </p>
            <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Guia
            </p>
          </div>
        </div>
      </div>

      {/* Badge de cancelamento */}
      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.5rem'
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#5DD4C3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
            Cancelamento grátis até 24h antes
          </span>
        </div>
      </div>
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Descrição */}
      {details.description && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            Sobre o passeio:
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {details.description}
          </p>
        </div>
      )}

      {/* O que está incluído */}
      {includes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            ✓ O que está incluído:
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {includes.map((item, idx) => (
              <span key={idx}>• {item}<br/></span>
            ))}
          </p>
        </div>
      )}

      {/* O que NÃO está incluído */}
      {notIncludes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            ✗ Não incluído:
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {notIncludes.map((item, idx) => (
              <span key={idx}>• {item}<br/></span>
            ))}
          </p>
        </div>
      )}

      {/* Itinerário */}
      {details.itinerary && details.itinerary.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            Itinerário:
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {details.itinerary.map((item, idx) => (
              <span key={idx}>• {item}<br/></span>
            ))}
          </p>
        </div>
      )}

      {/* Nota sobre refeições */}
      {details.meal_note && (
        <div style={{ 
          paddingTop: '0.5rem', 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Utensils size={16} color="rgba(255, 255, 255, 0.6)" strokeWidth={2} />
          <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {details.meal_note}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title={currentTour.title || 'Passeio'}
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      {/* Carrossel customizado para múltiplos tours */}
      <div style={{ position: 'relative', width: '100%', height: '240px', marginBottom: '1.5rem' }}>
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: '1rem',
          backgroundImage: `url(${images[0]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }} />

        {/* Navegação entre tours (se houver múltiplos) */}
        {tourList.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              style={{
                position: 'absolute',
                left: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(4px)',
                border: 'none',
                borderRadius: '50%',
                padding: '0.5rem',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              onClick={goToNext}
              style={{
                position: 'absolute',
                right: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(4px)',
                border: 'none',
                borderRadius: '50%',
                padding: '0.5rem',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>

            {/* Dots */}
            <div style={{
              position: 'absolute',
              bottom: '0.75rem',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '0.375rem'
            }}>
              {tourList.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: currentTourIndex === index ? '20px' : '6px',
                    height: '6px',
                    borderRadius: '9999px',
                    backgroundColor: currentTourIndex === index ? '#5DD4C3' : 'rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.2s'
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
